import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getServiceUserMedicationGroups,
  getServiceUserSingleMedicationGroup,
  getServiceUser,
} from "../services/users.js";
import { deleteMedicationGroup } from "../services/medication.js";
import Tabs from "../components/Tabs.js";
import { UserMedicationList } from "../components/Medications/UserMedicationList.js";
import { CreateMedicationGroup } from "../components/Medications/CreateMedicationGroup.js";
import { capitalize } from "../utils/functions.js";
import Spinner from "../components/PageLoader.js";
import EmptyState from "../components/EmptyState.js";
import RightSideModal from "../components/modals/RightSideModal.js";
import { WarningModal } from "../components/modals/WarningModal.js";
import { DeleteIcon } from "../components/icons/DeleteIcon.jsx";
import toast from "react-hot-toast";

export const UserMedications = () => {
  const [groupName, setGroupName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingGroupId, setLoadingGroupId] = useState(null); // Track which group is being loaded
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [showManageGroupsModal, setShowManageGroupsModal] = useState(false);
  const [editGroup, setEditGroup] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [warningMessage, setWarningMessage] = useState("");
  const [openWarning, setOpenWarning] = useState(false);
  const [user, setUser] = useState(null);
  const urlParam = useParams();

  const fetchMedicationGroups = async () => {
    if (!urlParam.id) {
      console.error("No user ID provided");
      toast.error("User ID is required");
      return;
    }

    console.log("Fetching medication groups for user ID:", urlParam.id);
    console.log("Base URL:", process.env.REACT_APP_BASE_URL);
    console.log("User from localStorage:", JSON.parse(localStorage.getItem("user") || "{}"));

    setIsLoading(true);
    try {
      const response = await getServiceUserMedicationGroups(urlParam.id);
      console.log("API Response:", response);

      const { data } = response;
      console.log("Medication groups data:", data);

      // Ensure data is an array before sorting
      const groupsArray = Array.isArray(data) ? data : [];
      console.log("Groups array:", groupsArray);

      // Sort medication groups alphabetically by name
      const sortedData = groupsArray.sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
      setUserGroups(sortedData);
      console.log("Successfully set user groups:", sortedData);
    } catch (error) {
      console.error("Error fetching medication groups for user ID:", urlParam.id);
      console.error("Full error object:", error);
      console.error("Error status:", error.response?.status);
      console.error("Error response data:", error.response?.data);
      console.error("Error message:", error.message);
      console.error("Request URL:", error.config?.url);

      const errorMessage = error.response?.status === 500
        ? `Server error (500): ${error.response?.data?.message || 'Internal server error'}`
        : "Failed to fetch medication groups";

      toast.error(errorMessage);
      setUserGroups([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSpecificMedicationGroup = async (groupId, groupIndex) => {
    setLoadingGroupId(groupId);
    try {
      const { data } = await getServiceUserSingleMedicationGroup(
        urlParam.id,
        groupId
      );

      // Update the specific group in the userGroups array
      setUserGroups((prevGroups) =>
        prevGroups.map((group, index) =>
          index === groupIndex
            ? { ...group, medications: data?.medications || [] }
            : group
        )
      );
    } catch (error) {
      console.error("Error fetching specific medication group:", error);
      console.error("Error details:", error.response?.data || error.message);
      // Don't show error toast if the group was just deleted - handle gracefully
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch medication group details");
      }
    } finally {
      setLoadingGroupId(null);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const { data } = await getServiceUser(urlParam.id);
      setUser(data);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details");
    }
  };

  const handleTabChange = (tabIndex) => {
    const selectedGroup = userGroups[tabIndex];
    if (selectedGroup) {
      fetchSpecificMedicationGroup(selectedGroup.id, tabIndex);
    }
  };

  useEffect(() => {
    if (!urlParam.id) return;

    const initializeData = async () => {
      try {
        await Promise.all([fetchMedicationGroups(), fetchUserDetails()]);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the first group's details when userGroups is populated
  useEffect(() => {
    if (userGroups.length > 0 && userGroups[0]?.id) {
      fetchSpecificMedicationGroup(userGroups[0].id, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroups.length]);

  const onEdit = (group, index) => {
    setEditGroup(true);
    setGroupName(group.name);
    setEditIndex(index);
    setOpenCreateGroupModal(true);
  };

  const onDelete = (index) => {
    setEditIndex(index);
    setWarningMessage("Are you sure you want to delete this medication group?");
    setOpenWarning(true);
  };

  const warningAction = async () => {
    // Capture index and group at start to prevent race conditions
    const currentIndex = editIndex;
    const groupToDelete = userGroups[currentIndex];

    if (!groupToDelete) {
      toast.error("Medication group not found");
      setOpenWarning(false);
      return;
    }

    setIsLoading(true);
    try {
      // Call the actual backend API
      await deleteMedicationGroup(urlParam.id, groupToDelete.id);

      // Update local state after successful deletion
      const updatedGroups = userGroups.filter((_, i) => i !== currentIndex);
      setUserGroups(updatedGroups);
      toast.success("Medication group deleted successfully");
    } catch (error) {
      console.error("Error deleting medication group:", error);
      toast.error("Failed to delete medication group");
    } finally {
      setIsLoading(false);
      setEditIndex(null);
      setOpenWarning(false);
    }
  };

  const tabItems = userGroups.map((group) => ({
    label: capitalize(group.name),
    content: (
      <UserMedicationList
        group={group}
        isLoading={loadingGroupId === group.id}
        medication={userGroups}
        userId={urlParam.id}
        reload={() => fetchMedicationGroups()}
      />
    ),
  }));

  return (
    <>
      {isLoading && <Spinner />}
      {!isLoading && userGroups.length === 0 && (
        <EmptyState
          title="No medication groups found"
          description="Create your first medication group to get started."
          actionButton={
            <button
              className="btn btn__primary"
              onClick={() => setOpenCreateGroupModal(true)}
            >
              + Add medication group
            </button>
          }
        />
      )}
      {!isLoading && userGroups.length > 0 && (
        <>
          <div className="user__top">
            <h5>
              {user?.firstName} {user?.lastName} - Medication
            </h5>
            <div className="ml-auto">
              <button
                className="btn btn__link btn__link-secondary"
                onClick={() => setShowManageGroupsModal(true)}
              >
                Manage medication group
              </button>
            </div>
          </div>
          <div>
            <Tabs
              tabs={tabItems}
              defaultActive={0}
              primaryColor="var(--primary)"
              grayColor="var(--gray)"
              onTabChange={handleTabChange}
            />
          </div>
        </>
      )}

      {openCreateGroupModal && (
        <CreateMedicationGroup
          setOpenCreateGroupModal={setOpenCreateGroupModal}
          openCreateGroupModal={openCreateGroupModal}
          userGroups={userGroups}
          currentUserId={urlParam.id}
          groupName={groupName}
          setGroupName={setGroupName}
          editGroup={editGroup}
          setEditGroup={setEditGroup}
          editIndex={editIndex}
          reload={fetchMedicationGroups}
        />
      )}

      <RightSideModal
        isOpen={showManageGroupsModal}
        onClose={() => setShowManageGroupsModal(false)}
        title="Manage Medication Groups"
      >
        <button
          className="btn btn__primary"
          onClick={() => setOpenCreateGroupModal(true)}
        >
          + Add new
        </button>
        <div className="medication-group__table">
          <table className="table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Medication</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {userGroups.map((group, index) => (
                <tr key={group.id || index}>
                  <td>
                    <span className="group-name">{group.name}</span>
                  </td>
                  <td>
                    <span className="medication-count">
                      {group.medications?.length || 0} medication
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn action-btn--edit"
                        onClick={() => onEdit(group, index)}
                        data-tooltip="Edit medication group"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn action-btn--delete"
                        onClick={() => onDelete(index)}
                        data-tooltip="Delete medication group"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </RightSideModal>

      {openWarning && (
        <WarningModal
          isOpen={openWarning}
          onCancel={() => setOpenWarning(false)}
          title="Warning"
          isLoading={isLoading}
          message={warningMessage}
          onConfirm={warningAction}
        />
      )}
    </>
  );
};
